import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { useAuth } from "@/auth/use-auth";
import type {
  SpiffPaginatedResponse,
  SpiffPagination,
} from "@/api/dto/pagination";
import type { SpiffProcessInstanceRow } from "@/api/dto/process-instance";
import {
  buildCaseReportMetadata,
  type CaseListFilters,
} from "@/api/endpoints/build-case-report-metadata";
import { qk } from "@/api/query-keys";
import type { CaseListItem } from "@/domain/case/case";
import { CASE_CONFIG, type CaseConfig } from "@/domain/case/case-config";
import { toCaseListItem } from "@/domain/case/map-process-instance";
import { refineCases } from "@/domain/case/refine-cases";

/** マイ案件一覧の取得パラメータ。 */
export type MyApplicationsParams = {
  /**
   * 起票者の username。`for-me` は関与案件を返すので、これで自分の分だけに絞る。
   * null なら絞らない（識別子が取れない状況で一覧を空にしない）。
   */
  readonly initiatorUsername?: string | null;
  readonly filters: CaseListFilters;
  readonly page: number;
  readonly perPage: number;
  readonly config?: CaseConfig;
};

export type MyApplicationsResult = {
  readonly items: readonly CaseListItem[];
  readonly pagination: SpiffPagination;
  /** クライアント側で再絞り込みしたため pagination.total が実件数より多いか。 */
  readonly isApproximateTotal: boolean;
};

const EMPTY_PAGINATION: SpiffPagination = { count: 0, total: 0, pages: 0 };

/**
 * マイ案件一覧（自分が起票した案件）。
 *
 * データ源は `POST /process-instances/for-me`。承認待ちタスクは「承認リクエスト」で
 * 扱うため、この一覧は「自分が出したもの」に限る（#29 の確定仕様）。
 *
 * ApiClient は queryKey に含めない（use-permissions と同じ理由）。
 */
export function myApplicationsQueryOptions(
  client: ApiClient,
  params: MyApplicationsParams,
) {
  const config = params.config ?? CASE_CONFIG;
  const { filters, page, perPage } = params;

  const fetchCases = async (): Promise<MyApplicationsResult> => {
    const response = await client.post<
      Partial<SpiffPaginatedResponse<SpiffProcessInstanceRow>>
    >(
      "/process-instances/for-me",
      {
        report_metadata: buildCaseReportMetadata(filters, config, {
          initiatorUsername: params.initiatorUsername ?? null,
        }),
      },
      { query: { page, per_page: perPage } },
    );

    const { items, isApproximateTotal } = refineCases(
      (response.results ?? []).map((row) => toCaseListItem(row, config)),
      { statuses: filters.statuses },
    );

    return {
      items,
      pagination: response.pagination ?? EMPTY_PAGINATION,
      isApproximateTotal,
    };
  };

  return queryOptions({
    queryKey: qk.processInstances.forMe({
      // 対象範囲（自分の起票 / 関与した案件）で結果が変わる
      involved: filters.involved ?? false,
      keyword: filters.keyword,
      statuses: filters.statuses,
      processModelId: filters.processModelId,
      submittedFrom: filters.submittedFrom,
      submittedTo: filters.submittedTo,
      page,
      perPage,
      // 利用者が変われば結果も変わる（キャッシュを混ぜない）
      initiator: params.initiatorUsername ?? null,
    }),
    queryFn: fetchCases,
    staleTime: 30_000,
  });
}

export function useMyApplications(
  params: MyApplicationsParams,
): UseQueryResult<MyApplicationsResult> {
  const { user } = useAuth();

  return useQuery(
    myApplicationsQueryOptions(useApiClient(), {
      initiatorUsername: user?.username ?? null,
      ...params,
    }),
  );
}
